// @flow
import { createSelector } from 'reselect';
import size from 'lodash/size';

import { staticCategories } from 'pages/MyAccount/services/reducers/categoriesReducer';
import { CATEGORY_ID } from 'pages/MyAccount/utils/categories';
import type { State } from 'pages/MyAccount/services/reducers/index';
import type { Category } from '../sharedTypes';

export const selectSponsoredProductsCategory = createSelector(
  (state, props) => state.productsState.sponsoredProductIds,
  (sponsoredProductIds) => {
    const sponsoredProductsCategory = size(sponsoredProductIds)
      ? [
          {
            id: CATEGORY_ID.sponsored,
            name: 'Free Gift!',
            children: [],
          },
        ]
      : [];
    return sponsoredProductsCategory;
  },
);

export const selectSelectedCategoryName: (State, { selectedCategoryId: string | null }) => string =
  createSelector(
    (state, props) => state.categoriesState.categories,
    (state, props) => selectSponsoredProductsCategory(state),
    (state, props) => props.selectedCategoryId,
    (categories, sponsoredProductsCategory, selectedCategoryId) =>
      findCategoryNameById(
        [...staticCategories, ...sponsoredProductsCategory, ...categories],
        selectedCategoryId,
      ),
  );

export const selectPathToSelectedCategory: (
  State,
  { selectedCategoryId: string | null },
) => Array<string> = createSelector(
  (state, props) => state.categoriesState.categories,
  (state, props) => selectSponsoredProductsCategory(state),
  (state, props) => props.selectedCategoryId,
  (categories, sponsoredProductsCategory, selectedCategoryId) =>
    findPathToCategory(
      [...categories, ...sponsoredProductsCategory, ...staticCategories],
      selectedCategoryId,
    ).map((categoryIdAndName) => categoryIdAndName[1]),
);

function findCategoryNameById(categories: Array<Category>, targetId: string | null): string {
  if (!categories.length) return '';
  const categoryName = categories.map((category) => {
    const categoryId = category.id && category.id.toString();
    if (categoryId === targetId) return category.name;
    const nestedCategoryName = findCategoryNameById(category.children, targetId);
    if (nestedCategoryName !== '') return nestedCategoryName;
    return '';
  });
  return categoryName;
}

export function findPathToCategory(
  categories: Array<Category>,
  targetId: string | null,
  path: Array<[string | null, string]> = [],
): Array<[string | null, string]> {
  if (!categories.length) return path;
  for (const category of categories) {
    path.push([category.id, category.name]);
    const categoryId = category.id && category.id.toString();
    if (categoryId === targetId) return path;
    findPathToCategory(category.children, targetId, path);
    const lastCategoryId = path[path.length - 1][0];
    if ((lastCategoryId && lastCategoryId.toString()) === targetId) return path;
    path.pop();
  }
  return path;
}
